import React from 'react';
import Cal from "@calcom/embed-react";
import type { UserProfile, Organization } from '@types';

interface ContactProps {
    currentUser: UserProfile | null;
    currentOrganization: Organization | null;
    onNavigate: (target: { page: string }) => void;
}

const Contact: React.FC<ContactProps> = ({ currentUser, currentOrganization, onNavigate }) => {
    const isGuest = !currentUser;

    return (
        <div className="min-h-screen bg-slate-50 flex flex-col">
            {/* Minimal Header for Guest/User navigation */}
            <header className="py-6 px-8 border-b border-slate-200 bg-white flex justify-between items-center sticky top-0 z-50">
                <div className="flex items-center gap-3">
                    <div className="h-6 w-1 bg-blue-600 rounded-full"></div>
                    <span className="font-bold text-slate-900 tracking-tight text-lg uppercase">
                        IntelOptics <span className="text-blue-600">Briefing Portal</span>
                    </span>
                </div>
                <button 
                    onClick={() => onNavigate({ page: isGuest ? 'marketing' : 'organizationDashboard' })}
                    className="flex items-center gap-2 text-[11px] font-black uppercase text-slate-500 hover:text-blue-600 transition-colors"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor" className="w-3.5 h-3.5"><path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" /></svg>
                    Return to {isGuest ? 'Strategic Portal' : 'Command Center'}
                </button>
            </header>

            <main className="flex-1 flex flex-col items-center justify-center p-6 md:p-12">
                <div className="w-full max-w-6xl bg-white rounded-[2.5rem] border border-slate-200 shadow-2xl shadow-slate-200/50 overflow-hidden flex flex-col lg:flex-row">
                    
                    {/* Briefing Info Panel */}
                    <div className="lg:w-1/3 bg-slate-50 p-10 md:p-16 text-slate-900 flex flex-col justify-between border-b lg:border-b-0 lg:border-r border-slate-200">
                        <div>
                            <h1 className="text-3xl md:text-4xl font-black mb-6 uppercase tracking-tight leading-tight">
                                IntelOptics <br />
                                <span className="text-blue-600">Scheduler</span>
                            </h1>
                            <p className="text-slate-600 text-sm leading-relaxed mb-10 font-medium">
                                {isGuest 
                                    ? "Inteloptics provides geopolitical analysis and strategic consultation and system integration briefings. Please inquire"
                                    : "Schedule a high-fidelity system review or strategic consultation with our senior intelligence analysts. Our team is available for deep-dive workflow optimizations and mission-planning briefings."
                                }
                            </p>

                            <div className="space-y-6 border-t border-slate-200 pt-10">
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 bg-white border border-slate-100 rounded-xl flex items-center justify-center text-blue-500 shrink-0 shadow-sm">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m24.402-2.247c.282.659.444 1.381.444 2.147 0 2.143-.438 4.18-1.23 6.031" /></svg>
                                    </div>
                                    <div>
                                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Global Coverage</div>
                                        <div className="text-sm font-bold text-slate-700">24/7 Intelligence Support</div>
                                    </div>
                                </div>
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 bg-white border border-slate-100 rounded-xl flex items-center justify-center text-indigo-500 shrink-0 shadow-sm">
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z" /></svg>
                                    </div>
                                    <div>
                                        <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Verification</div>
                                        <div className="text-sm font-bold text-slate-700">Validated Analyst Protocols</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>

                    {/* Cal.com Interface Panel */}
                    <div className="lg:w-2/3 bg-white overflow-hidden relative min-h-[700px]">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(59,130,246,0.03),transparent)] pointer-events-none"></div>
                        <Cal
                            calLink="inteloptics"
                            style={{ 
                                width: "100%", 
                                height: "100%", 
                                border: "none"
                            }}
                            config={{ 
                                theme: 'light',
                                layout: 'month_view'
                            }}
                        />
                    </div>
                </div>

                <div className="mt-12 text-center text-slate-400">
                    <p className="text-[11px] font-bold uppercase tracking-widest flex items-center justify-center gap-4">
                        <a href="mailto:contact@eprims.com" className="hover:text-blue-600 transition-colors underline decoration-blue-600/30">Direct Email Protocol</a>
                        <span className="text-slate-200">|</span>
                        <a href="tel:8564250168" className="hover:text-blue-600 transition-colors underline decoration-blue-600/30">Secure Voice Channel</a>
                    </p>
                </div>
            </main>
        </div>
    );
};

export default Contact;
